import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  Copy as CopyIcon,
  Filter as FilterIcon,
  LayoutGrid,
  List as ListIcon,
  Plus,
  Search,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/layout/page-header';
import { Pagination } from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermission } from '@/features/auth/auth.store';
import {
  BUILT_IN_FILTERS,
  CATALOG_SORTS,
  useCatalogSearch,
  type CatalogProduct,
  type CatalogSearchModel,
} from '@/features/search-optimization/catalog-search.api';
import { cn } from '@/lib/utils';
import {
  ItemFiltersSheet,
  ItemGtinsSheet,
  ItemLogsSheet,
  ItemMediaSheet,
  ItemRelationsSheet,
  ManageFiltersSheet,
  itemLabel,
} from './item-drawers';
import type { AssignedStore } from './item-master.api';
import { ItemEditorSheet } from './item-editor';
import { BusinessItemEditor } from './business-item-editor';
import { CopyCatalogSheet } from './copy-catalog-sheet';
import { MetadataSheet } from './metadata-sheet';
import { useCatalogFilters, type MasterItem } from './item-master.api';

/**
 * Item Master — the catalogue.
 *
 * The page is a search: everything on it exists to find one item, and the item
 * is then opened. It shares its search with Search Optimisation, which asks the
 * same endpoint the same way — one module, so the two cannot drift.
 */

type ListKind =
  | 'gtins'
  | 'media'
  | 'filters'
  | 'recommendation'
  | 'substitute'
  | 'logs'
  | 'metadata';

export function ItemMasterPage() {
  const { t, i18n } = useTranslation();
  const isEnglish = (i18n.resolvedLanguage ?? 'en') === 'en';
  const isAdmin = usePermission('isSuperAdmin');

  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [sort, setSort] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [view, setView] = useState<'table' | 'grid'>('table');

  const [editing, setEditing] = useState<MasterItem | 'new' | null>(null);
  const [list, setList] = useState<{ kind: ListKind; item: MasterItem } | null>(
    null
  );
  const [manageFilters, setManageFilters] = useState(false);
  const [copyCatalog, setCopyCatalog] = useState(false);
  const [offer, setOffer] = useState<{
    store: AssignedStore;
    others: AssignedStore[];
    item: MasterItem;
  } | null>(null);

  const businessFilters = useCatalogFilters();

  const model: CatalogSearchModel = useMemo(
    () => ({
      Query: query,
      Page: page,
      Size: size,
      Filters: selected,
      Sort: sort,
    }),
    [query, page, size, selected, sort]
  );

  const search = useCatalogSearch(model);
  const products = search.data?.items ?? [];
  const total = search.data?.count ?? 0;

  const filters = useMemo(
    () => [
      ...BUILT_IN_FILTERS.map((tag) => ({
        key: tag,
        label: t(`searchOptimization.filter.${tag}` as const),
      })),
      ...(businessFilters.data ?? []).map((filter) => ({
        key: filter.id,
        label: (isEnglish ? filter.name : filter.nameSpa) || filter.name || '—',
      })),
    ],
    [businessFilters.data, isEnglish, t]
  );

  /**
   * The search returns a catalogue row, not the full item — so opening one
   * carries what it has and the editor fills the rest from its own calls.
   */
  const toItem = (product: CatalogProduct): MasterItem =>
    ({
      id: product.id,
      gtin: product.gtin,
      brand: product.brand,
      name: product.name,
      nameSpa: null,
      description: product.description,
      descriptionSpa: null,
      itemImage: product.itemImage,
      itemThumbnail: null,
      searchTags: product.searchTags,
      internalId: product.internalId,
      internalRank: null,
      suggestedDepartment: null,
      isDeleted: false,
      isPackagingSensitive: false,
      isTemperatureSensitive: false,
      isAlcoholicBeverage: false,
      isPerishable: false,
      isEbt: false,
      idRequired: false,
      hasPluCode: false,
      createdAt: null,
      editedAt: null,
      itemMedias: null,
      itemRecommendations: null,
      gtins: null,
    }) satisfies MasterItem;

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('itemMaster.title')}
        description={t('itemMaster.subtitle')}
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setManageFilters(true)}
            >
              <FilterIcon />
              <span className="hidden sm:inline">
                {t('itemMaster.manageFilters')}
              </span>
            </Button>
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCopyCatalog(true)}
              >
                <CopyIcon />
                <span className="hidden sm:inline">
                  {t('itemMaster.copyCatalog')}
                </span>
              </Button>
            )}
            {isAdmin && (
              <Button size="sm" onClick={() => setEditing('new')}>
                <Plus />
                <span className="hidden sm:inline">
                  {t('itemMaster.createNew')}
                </span>
              </Button>
            )}
          </>
        }
      />

      {/* ------------------------------------------------------ filters */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {businessFilters.isPending ? (
            <Skeleton className="h-6 w-64" />
          ) : (
            <>
              {filters.map((filter) => {
                const active = selected.includes(filter.key);
                return (
                  <button
                    key={filter.key}
                    type="button"
                    aria-pressed={active}
                    disabled={search.isFetching}
                    onClick={() => {
                      setPage(0);
                      setSelected((current) =>
                        current.includes(filter.key)
                          ? current.filter((item) => item !== filter.key)
                          : [...current, filter.key]
                      );
                    }}
                    className={cn(
                      'rounded-full border px-2.5 py-1 text-xs transition-colors disabled:opacity-50',
                      active
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'hover:bg-accent'
                    )}
                  >
                    {filter.label}
                  </button>
                );
              })}
              {selected.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelected([]);
                    setPage(0);
                  }}
                >
                  {t('itemMaster.clearFilters')}
                </Button>
              )}
            </>
          )}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row">
          <form
            role="search"
            className="flex flex-1 gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (draft === query && page === 0) void search.refetch();
              setQuery(draft);
              setPage(0);
            }}
          >
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={t('itemMaster.searchPlaceholder')}
                aria-label={t('itemMaster.searchPlaceholder')}
                className="h-10 pl-9"
              />
            </div>
            <Button
              type="submit"
              className="h-10"
              disabled={search.isFetching}
            >
              <Search className="sm:hidden" />
              <span className="hidden sm:inline">{t('common.search')}</span>
            </Button>
          </form>

          <div className="flex gap-2">
            <Select
              value={String(sort)}
              disabled={search.isFetching}
              onValueChange={(value) => {
                setSort(Number(value));
                setPage(0);
              }}
            >
              <SelectTrigger className="h-10 flex-1 sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATALOG_SORTS.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {t(option.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex shrink-0 rounded-lg border p-0.5">
              <Button
                variant={view === 'table' ? 'secondary' : 'ghost'}
                size="icon-sm"
                aria-label={t('itemMaster.tableView')}
                aria-pressed={view === 'table'}
                onClick={() => setView('table')}
              >
                <ListIcon className="size-4" />
              </Button>
              <Button
                variant={view === 'grid' ? 'secondary' : 'ghost'}
                size="icon-sm"
                aria-label={t('itemMaster.gridView')}
                aria-pressed={view === 'grid'}
                onClick={() => setView('grid')}
              >
                <LayoutGrid className="size-4" />
              </Button>
            </div>
          </div>
        </div>

        {search.isSuccess && (
          <p className="text-muted-foreground text-sm">
            {t('itemMaster.showingOf', { shown: products.length, total })}
          </p>
        )}
      </div>

      {search.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('itemMaster.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void search.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {search.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <Skeleton className="size-14 shrink-0 rounded" />
              <Skeleton className="h-4 w-64" />
            </div>
          ))}
        </div>
      )}

      {search.isSuccess && products.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={search.isFetching} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {t('itemMaster.noResults')}
          </p>
        </div>
      )}

      {products.length > 0 && (
        <div className="space-y-3">
          <div className="relative rounded-xl">
            <BusyOverlay show={search.isFetching} />
            <div
              className={cn(
                search.isFetching && 'opacity-60 transition-opacity'
              )}
            >
              {view === 'table' ? (
                <div className="divide-y rounded-xl border">
                  {products.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => setEditing(toItem(product))}
                      className="hover:bg-accent/40 flex w-full items-center gap-3 p-3 text-left transition-colors"
                    >
                      {product.itemImage ? (
                        <img
                          src={product.itemImage}
                          alt=""
                          className="bg-muted size-14 shrink-0 rounded object-contain"
                        />
                      ) : (
                        <span className="bg-muted size-14 shrink-0 rounded" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {itemLabel(product)}
                        </p>
                        <p className="text-muted-foreground truncate text-xs">
                          {product.description}
                        </p>
                        <p className="text-muted-foreground truncate font-mono text-xs">
                          {[product.gtin, product.internalId]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                      </div>
                      {product.searchTags && (
                        <Badge variant="secondary" className="shrink-0">
                          {t('itemMaster.tagged')}
                        </Badge>
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                  {products.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      onClick={() => setEditing(toItem(product))}
                      className="bg-card hover:border-primary/50 flex flex-col gap-2 rounded-xl border p-3 text-left transition-colors"
                    >
                      <p className="text-muted-foreground truncate font-mono text-xs">
                        {product.gtin}
                      </p>
                      {product.itemImage ? (
                        <img
                          src={product.itemImage}
                          alt=""
                          className="bg-muted h-20 w-full rounded object-contain"
                        />
                      ) : (
                        <span className="bg-muted h-20 w-full rounded" />
                      )}
                      <div className="min-w-0">
                        <p className="text-muted-foreground truncate text-xs">
                          {product.brand}
                        </p>
                        <p className="truncate text-sm">{product.name}</p>
                        <p className="text-muted-foreground truncate text-xs">
                          {product.description}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <Pagination
            page={page}
            size={size}
            total={total}
            onPageChange={setPage}
            onSizeChange={(next) => {
              setSize(next);
              setPage(0);
            }}
          />
        </div>
      )}

      <ItemEditorSheet
        item={editing}
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
        onOpenList={(kind, item) => setList({ kind, item })}
        onOpenStore={(store, others) =>
          setOffer(
            editing && editing !== 'new'
              ? { store, others, item: editing }
              : null
          )
        }
        onCreated={(created) => setEditing(created)}
      />

      <BusinessItemEditor
        store={offer?.store ?? null}
        item={offer?.item ?? null}
        otherStores={offer?.others ?? []}
        open={!!offer}
        onOpenChange={(next) => {
          if (!next) setOffer(null);
        }}
      />

      <MetadataSheet
        item={list?.kind === 'metadata' ? list.item : null}
        open={list?.kind === 'metadata'}
        onOpenChange={(next) => {
          if (!next) setList(null);
        }}
      />

      <CopyCatalogSheet open={copyCatalog} onOpenChange={setCopyCatalog} />

      <ItemGtinsSheet
        itemId={list?.kind === 'gtins' ? list.item.id : null}
        title={list ? itemLabel(list.item) : t('itemMaster.gtins')}
        open={list?.kind === 'gtins'}
        onOpenChange={(next) => {
          if (!next) setList(null);
        }}
      />

      <ItemMediaSheet
        itemId={list?.kind === 'media' ? list.item.id : null}
        title={list ? itemLabel(list.item) : t('itemMaster.media')}
        open={list?.kind === 'media'}
        onOpenChange={(next) => {
          if (!next) setList(null);
        }}
      />

      <ItemFiltersSheet
        itemId={list?.kind === 'filters' ? list.item.id : null}
        title={list ? itemLabel(list.item) : t('itemMaster.filters')}
        open={list?.kind === 'filters'}
        onOpenChange={(next) => {
          if (!next) setList(null);
        }}
      />

      <ItemRelationsSheet
        kind={list?.kind === 'substitute' ? 'substitute' : 'recommendation'}
        itemId={
          list?.kind === 'recommendation' || list?.kind === 'substitute'
            ? list.item.id
            : null
        }
        title={list ? itemLabel(list.item) : ''}
        open={list?.kind === 'recommendation' || list?.kind === 'substitute'}
        onOpenChange={(next) => {
          if (!next) setList(null);
        }}
      />

      <ItemLogsSheet
        itemId={list?.kind === 'logs' ? list.item.id : null}
        title={list ? itemLabel(list.item) : t('itemMaster.logs')}
        open={list?.kind === 'logs'}
        onOpenChange={(next) => {
          if (!next) setList(null);
        }}
      />

      <ManageFiltersSheet
        open={manageFilters}
        onOpenChange={setManageFilters}
      />
    </div>
  );
}
